import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/utils/cn'

const THREAD_TABS = ['active', 'archived'] as const

export type ThreadTab = (typeof THREAD_TABS)[number]

type ThreadTabsProps = {
  tab: ThreadTab
  onTabChange: (tab: ThreadTab) => void
  activeCount: number
  archivedCount: number
  /**
   * The panel these tabs control. Also namespaces the tabs' own ids, so the
   * full page and the patient chart's drawer can both be mounted without
   * colliding on `thread-tab-active`.
   */
  panelId: string
  /**
   * Controls for the far end of the tab row — the drawer puts its close, filter
   * and Create Thread here rather than in a row of their own, which is a whole
   * band of vertical space in a panel that has none to spare.
   *
   * Deliberately a sibling of the tablist, not a child: a `role="tablist"` may
   * contain tabs and nothing else, and arrow-key navigation is scoped to it.
   */
  actions?: ReactNode
}

/**
 * `Active | Archived` for a conversation list — the ARIA tabs pattern, with a
 * roving tabindex: the tablist is a single tab stop and Left/Right move within
 * it.
 *
 * Shared by the Messages page and the patient chart's drawer, which used to
 * show the same two groups as a pair of always-expanded accordions. One
 * implementation because the keyboard contract is the part worth not writing
 * twice.
 */
export const ThreadTabs = ({
  tab,
  onTabChange,
  activeCount,
  archivedCount,
  panelId,
  actions,
}: ThreadTabsProps) => {
  const tabRefs = useRef<Partial<Record<ThreadTab, HTMLButtonElement | null>>>({})

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = THREAD_TABS.indexOf(tab)
    let nextIndex = currentIndex

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % THREAD_TABS.length
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + THREAD_TABS.length) % THREAD_TABS.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = THREAD_TABS.length - 1
    } else {
      return
    }

    event.preventDefault()
    const nextTab = THREAD_TABS[nextIndex]
    onTabChange(nextTab)
    tabRefs.current[nextTab]?.focus()
  }

  const renderTab = (id: ThreadTab, label: string, count: number) => {
    const isActive = tab === id
    return (
      <button
        key={id}
        type="button"
        role="tab"
        id={threadTabId(panelId, id)}
        ref={(node) => {
          tabRefs.current[id] = node
        }}
        aria-selected={isActive}
        aria-controls={panelId}
        tabIndex={isActive ? 0 : -1}
        onClick={() => onTabChange(id)}
        className={cn([
          'behuman-v2-mulish relative flex items-center gap-2 px-4 py-3 !text-[15px] transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#E5533D]',
          isActive
            ? '!font-bold text-[#E5533D]'
            : '!font-semibold text-[#6B7280] hover:text-[#1F2733]',
        ])}
      >
        {label}
        <span
          aria-hidden
          className={cn([
            'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold',
            isActive ? 'bg-[#FCF1EE] text-[#E5533D]' : 'bg-[#F1F2F4] text-[#6B7280]',
          ])}
        >
          {count}
        </span>
        <span className="sr-only">{`${count} conversations`}</span>
        {isActive && (
          <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#E5533D]" />
        )}
      </button>
    )
  }

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-[#ECEEF1]">
      <div
        role="tablist"
        aria-label="Conversations"
        onKeyDown={handleKeyDown}
        className="flex shrink-0 px-2"
      >
        {renderTab('active', 'Active', activeCount)}
        {renderTab('archived', 'Archived', archivedCount)}
      </div>
      {actions && (
        <div className="ml-auto flex min-w-0 items-center justify-end gap-2 pr-2">{actions}</div>
      )}
    </div>
  )
}

/** The id of one tab button — for the panel's `aria-labelledby`. */
// eslint-disable-next-line react-refresh/only-export-components
export const threadTabId = (panelId: string, tab: ThreadTab) => `${panelId}-tab-${tab}`
